import { useRef } from "react";
import { useQueries } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { Loader } from "rsuite";
import { axInst } from "../../../Components/Login/Service";
import WaterMark from "../../../Components/WaterMark/WaterMark.jsx";
import TwoComparisionBarChart from "../../../Components/Charts/TwoComparisionBarChart";
import TqpCard from "../../../Components/Cards/TqpCards";
import { captureAndAddToPpt } from "../../captureAndAddToPpt.js";

const fetchChartData = async (apiEndpoint) => {
  const response = await axInst.post(apiEndpoint);
  if (response.status !== 200) {
    throw new Error("Failed to fetch data");
  }
  return response.data;
};

const fetchTqpData = async (tqpEndpoints) => {
  const response = await axInst.post(tqpEndpoints);
  if (response.status !== 200) {
    throw new Error("Failed to fetch data");
  }
  return response.data;
};

const TqpLostCase = () => {
  const navigate = useNavigate();
  const captureRef = useRef(null);
  const headingRef = useRef(null);
  const buttonRefs = useRef([]);

  const apiEndpoints = [
    "/wsr/tqp_hcv_truck",
    "/wsr/tqp_ilmcv_truck",
    "/wsr/tqp_cvp",
    "/wsr/tqp_scv_pu",
  ];

  const tqpEndpoints = ["/wsr/lost_cases", "/wsr/salient"];

  const chartQueries = useQueries({
    queries: apiEndpoints.map((endpoint) => ({
      queryKey: ["chartData", endpoint],
      queryFn: () => fetchChartData(endpoint),
      refetchIntervalInBackground: true,
    })),
  });

  const tqpQueries = useQueries({
    queries: tqpEndpoints.map((tqpEndpoint) => ({
      queryKey: ["tqp", tqpEndpoint],
      queryFn: () => fetchTqpData(tqpEndpoint),
      refetchIntervalInBackground: true,
    })),
  });

  const chartHeadings = [
    "HCV Truck",
    "ILMCV Truck ",
    "CV Passenger",
    "SCV & PU",
  ];

  const handleCaptureAndAddToPpt = () => {
    captureAndAddToPpt({
      element: captureRef.current,
      buttonRefs: buttonRefs.current,
      headingRef: headingRef.current,
      fileName: "Top 5 Applications TQP (MTD vs LMTD)",
    });
  };

  return (
    <div ref={captureRef} style={{ backgroundColor: "#f2f5f9" }}>
      <div
        ref={headingRef}
        className="bg-[#2A5775] text-center text-white rounded-md font-bold mx-2 p-1 hidden"
      >
        Top 5 Applications TQP (MTD vs LMTD)
      </div>
      <div className="md:flex w-full gap-2 p-2 block">
        <div className="md:w-1/5 md:flex flex-col gap-2 w-full">
          {tqpQueries.map((query, index) => (
            <TqpCard
              key={index}
              heading={
                index === 0
                  ? "MTD Validated Lost Cases"
                  : "T0P 5 Application TQP Salience"
              }
              data={query.data || {}}
            />
          ))}
        </div>
        <section
          className={`grid gap-3 md:w-4/5  overflow-hidden ${
            chartQueries.filter((q) => q.data?.length > 0 && !q.isError)
              .length > 1
              ? "grid-cols-2"
              : "grid-cols-1"
          }`}
        >
          {chartQueries.map((query, index) => {
            if (query.isLoading) {
              return (
                <div key={index} className="bg-white rounded-md p-2">
                  <WaterMark />
                  <div className="grid items-center justify-center w-full h-full">
                    <Loader />
                  </div>
                </div>
              );
            }

            if (
              query.isError ||
              !Array.isArray(query.data) ||
              query.data.length === 0
            ) {
              return null;
            }

            return (
              <div
                key={`${index}-${query.data?.length}`}
                className="relative bg-white rounded-md p-2"
              >
                <WaterMark />
                <TwoComparisionBarChart
                  data={query.data}
                  heading={chartHeadings[index]}
                  legendData={["MTD", "LMTD"]}
                  ChartHeight="220px"
                />
              </div>
            );
          })}
        </section>
      </div>
      <section className="flex justify-center gap-5">
        <button
          className="bg-[#2A5775] text-white font-semibold py-1 px-4 rounded hover:bg-[#2898e2] focus:outline-none focus:ring-2 focus:ring-blue-300 transition-all duration-300"
          ref={(el) => {
            buttonRefs.current[1] = el;
          }}
          onClick={handleCaptureAndAddToPpt}
          title="Capture screenshot to add to PowerPoint"
        >
          Capture Screenshot
        </button>
        <button
          ref={(el) => {
            buttonRefs.current[4] = el;
          }}
          className="bg-[#2A5775] text-white font-semibold py-1 px-4 rounded hover:bg-[#2898e2] focus:outline-none focus:ring-2 focus:ring-blue-300 transition-all duration-300"
          title="Performance Dashboard"
          onClick={() => navigate("/Performance-Dashboard")}
        >
          Performance
        </button>
        <button
          className="bg-[#2A5775] text-white font-semibold py-1 px-4 rounded hover:bg-[#2898e2] focus:outline-none focus:ring-2 focus:ring-blue-300 transition-all duration-300"
          ref={(el) => {
            buttonRefs.current[12] = el;
          }}
          title="WSR"
          onClick={() => navigate("/WSR")}
        >
          Weekly Sales Review
        </button>
        <button
          ref={(el) => {
            buttonRefs.current[2] = el;
          }}
          className="bg-[#2A5775] text-white font-semibold py-1 px-4 rounded hover:bg-[#2898e2] focus:outline-none focus:ring-2 focus:ring-blue-300 transition-all duration-300"
          title="Wsr Metrics"
          onClick={() => navigate("/WSR-Metrics")}
        >
          Metrics
        </button>
        <button
          ref={(el) => {
            buttonRefs.current[3] = el;
          }}
          className="bg-[#2A5775] text-white font-semibold py-1 px-4 rounded hover:bg-[#2898e2] focus:outline-none focus:ring-2 focus:ring-blue-300 transition-all duration-300"
          title="State wise Analysis of Retail & Offtake"
          onClick={() => navigate("/State-Offtake-Retails")}
        >
          State wise Analysis of Retail & Offtake
        </button>
      </section>
    </div>
  );
};

export default TqpLostCase;
